/* eslint-disable react/prop-types */
import { TextField } from "@mui/material";
import { ChangeEvent, FC, forwardRef, useContext, useState } from "react";
import { IMaskInput } from "react-imask";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { PPProps, PPTextFieldProps } from "./PPField";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import { funcExecutor } from "../../functions/generic-functions";
import { cloneDeep, isEqual } from "lodash";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCurrency = forwardRef<HTMLElement, CustomProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={"$num"}
      blocks={{
        num: {
          mask: Number,
          thousandsSeparator: ",",
          scale: 2,
          signed: false,
          normalizeZeros: true,
          radix: ".",
          padFractionalZeros: true,
        },
      }}
      lazy={false}
      inputRef={ref as any}
      onAccept={(value: any) =>
        onChange({ target: { name: props.name, value } })
      }
      overwrite
    />
  );
});

TextMaskCurrency.displayName = "TextMaskCurrency";

interface State {
  textmask: string;
}

type InnerTextFieldProps = {
  props: PPTextFieldProps;
  field: ControllerRenderProps<FieldValues, string>;
};

const InnerCurrencyField: FC<InnerTextFieldProps> = ({ props, field }) => {
  const invalidCurrencyFormat: string = "Invalid Currency format";
  const fontColor: string = "text.secondary";
  const fieldIsRequired: string = "Field is required";
  const invalidFormat: string = "Invalid format";
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];

  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (appContext: AppContextType) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext
        );
      });
    }
  };

  const [errorMessage, setErrorMessage]: [
    string,
    React.Dispatch<React.SetStateAction<string>>
  ] = useState(() => fieldIsRequired);

  const isFieldInvalid: boolean =
    appContext.fieldState[field.name] !== undefined &&
    !appContext.fieldState[field.name];
  const isHideField: boolean =
    appContext.fieldHide[field.name] !== undefined &&
    appContext.fieldHide[field.name];
  const isDisabledField: boolean =
    appContext.fieldDisable[field.name] !== undefined &&
    appContext.fieldDisable[field.name];

  //   const [values, setValues] = useState<State>({
  //     textmask: "",
  //   });

  return (
    <Controller
      render={({ field }) => (
        <TextField
          {...field}
          sx={isHideField ? { display: "none" } : null}
          disabled={isDisabledField}
          InputProps={{
            name: "textmask",
            inputComponent: TextMaskCurrency as any,
            inputProps: {
              component: IMaskInput,
            },
            value: field.value,
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              field.onChange(event.target.value);

              const currencyRegEx =
                /^\$([0-9]{1, 3}(\, [0-9]{3})*|([0-9]+))(\.[0-9]{2})?$/;
              let newAppContext: AppContextType = cloneDeep(appContext);
              const isValidFormat: boolean = currencyRegEx.test(
                event.target.value
              );
              newAppContext.fieldState[field.name] =
                (isRequired &&
                  event.target.value.length > 0 &&
                  isValidFormat) ||
                (!isRequired &&
                  (isValidFormat || event.target.value.length == 0));
              setErrorMessage(
                isValidFormat ? fieldIsRequired : invalidCurrencyFormat
              );
              onChange(newAppContext);
              if (!isEqual(appContext, newAppContext))
                setAppContext(newAppContext);


              return event.target.value;
            },
          }}
          variant="outlined"
          id={props.id}
          label={props.label + (props.required ? " *" : "")}
          fullWidth
        />
      )}
      name={props.id}
      control={control}
    />
  );
};

const Currency: FC<PPTextFieldProps> = (props) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={props.id}
      defaultValue={""}
      control={control}
      render={({ field }) => {
        return (
          <InnerCurrencyField props={props} field={field}></InnerCurrencyField>
        );
      }}
    />
  );
};

export default Currency;
//Currency.whyDidYouRender = true;
